import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';

import { getCloudinaryFlavoredImage } from 'lib/imageUtils';
import { getPublisher } from 'lib/headerMetadata';
import { cloudinaryFlavorLd } from './cloudinaryFlavorLd';
import { getContentUrl } from './getContentUrl';
import { buildVideoMediaObject } from './buildVideoMediaObject';

dayjs.extend(durationPlugin);

/**
 * Creates an LD JSON object from properties passed in. Options argument can be used to specify if
 * properties for live video should be added, which allows the "live flag" on google SERP
 * @param  {object} videoData object containing values that should be used for the ld+json
 * representation
 * @param  {object} options Options object
 * @param  {boolean} options.includePublicationData whether to include the `publication` property in the returned
 * ld+json to indicate to Google et al that the video is a live stream
 * @param  {boolean} options.includeMainEntityOfPage whether to include the `mainEntityOfPage`
 * property in the returned ld+json to indicate to Google et al that this video is the main entity
 * of the webpage determined by `url` in the `videoData` object
 * @returns {object} ld+json that should be stringified for use in the metadata of a web page
 */
export function buildVideoObjectLD(videoData, options) {
  const {
    author,
    closedCaptioning,
    closedCaptioningContent,
    contentUrl,
    description,
    duration,
    endDate,
    genre,
    isLiveBroadcast,
    name,
    startDate,
    publisher,
    thumbnailUrl,
    url,
    uploadDate,
    showName,
  } = videoData;

  const includePublicationData = options?.includePublicationData;
  const includeMainEntityOfPage = options?.includeMainEntityOfPage;
  const closedCaptioningText = closedCaptioningContent?.text || null;
  const closedCaptioningVTT = closedCaptioning?.webvtt || null;
  const closedCaptioningSRT = closedCaptioning?.srt || null;
  const caption = [
    buildVideoMediaObject(closedCaptioningSRT, 'srt'),
    buildVideoMediaObject(closedCaptioningVTT, 'vtt'),
  ].filter((c) => c !== null);

  // if `liveVideoStatus` is anything other than `NONE`, we can go ahead and add the props to the
  // json-ld. even if it's `UPCOMING` or `CONCLUDED`, we can still include the additional props
  const liveBroadcastEvent = includePublicationData && {
    publication: [
      {
        '@type': 'BroadcastEvent',
        ...(showName && { name: showName }),
        isLiveBroadcast,
        startDate,
        endDate,
      },
    ],
  };

  const mainEntityOfPage = includeMainEntityOfPage && {
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  return {
    '@context': 'http://schema.org',
    '@type': 'VideoObject',
    '@id': url,
    name,
    description,
    author,
    thumbnailUrl,
    contentUrl,
    duration,
    publisher,
    uploadDate,
    genre,
    url,
    ...liveBroadcastEvent,
    ...mainEntityOfPage,
    ...(closedCaptioningText?.length > 0 && { transcript: closedCaptioningText.join(' ') }),
    ...(caption?.length > 0 && { caption }),
  };
}

/**
 * Use duration and dateBroadcast to determine endDate for a video
 * @param  {string|null} duration String in ISO8601 duration format
 * @param  {string|null} dateBroadcast
 * @returns {boolean|string} String in ISO8601 format
 */
const getEndDateFromData = (duration, dateBroadcast) => {
  if (!duration) {
    return false;
  }
  const dayjsStartTime = dayjs(dateBroadcast);
  const dayjsDuration = dayjs.duration(duration);
  return dayjsStartTime.isValid()
    && !Number.isNaN(dayjsDuration.asMilliseconds())
    && dayjsStartTime
      .add(dayjsDuration.asMilliseconds(), 'ms')
      .toISOString();
};

/**
 * Takes Video data from Bento API response to create an LD+JSON representation. Basically the same
 * as {@link buildVideoObjectLD} but with a mapping layer from Bento API data
 * @param {Video} video Video from Bento API response. (This type is the Video type from the
 * auto-generated GraphQL jsdoc types found in `lib/graphqlTypeDefs.js`)
 * @param {object} options
 * @param {boolean} options.includeMainEntityOfPage passed through to `buildVideoObjectLD` to turn
 * on the inclusion of `mainEntityOfPage` prop in the LD JSON object
 * @returns {object} Object of LD JSON data. should be JSON.stringify()'d into a script tag
 */
export function buildVideoObjectLDFromApiData({
  video, options, vertical, section,
}) {
  const {
    headline: {
      primary: videoHeadlinePrimary,
      seo: videoHeadlineSeo,
      social: videoHeadlineSocial,
      tease: videoHeadlineTease,
    } = {},
    url: {
      primary: primaryUrl,
    } = {},
    description: {
      primary: videoDescriptionPrimary,
      seo: videoDescriptionSeo,
      social: videoDescriptionSocial,
      tease: videoDescriptionTease,
    } = {},
    duration,
    date,
    primaryImage: {
      url: {
        primary: videoImageUrl,
      } = {},
    } = {},
    videoAssets,
    liveVideoStatus,
    dateBroadcast,
    closedCaptioning,
  } = video;

  const contentUrl = getContentUrl(videoAssets);

  const cloudinaryImageUrl = getCloudinaryFlavoredImage(videoImageUrl, cloudinaryFlavorLd);

  // 'CONCLUDED', 'LIVE', or 'UPCOMING'
  const includePublicationData = typeof liveVideoStatus === 'string' && liveVideoStatus !== 'NONE';

  const endDate = includePublicationData
    ? (
      getEndDateFromData(duration, dateBroadcast)
      || dayjs().add(1, 'day').startOf('hour').toISOString()
    )
    : undefined;

  const includeMainEntityOfPage = options?.includePublicationData;

  const videoObjectData = {
    '@context': 'http://schema.org',
    '@type': 'VideoObject',
    '@id': primaryUrl,
    name:
      videoHeadlineSeo
      || videoHeadlineSocial
      || videoHeadlinePrimary
      || videoHeadlineTease,
    description:
      videoDescriptionSeo
      || videoDescriptionSocial
      || videoDescriptionPrimary
      || videoDescriptionTease,
    thumbnailUrl: [cloudinaryImageUrl],
    contentUrl,
    duration,
    publisher: getPublisher(vertical, section),
    uploadDate: date?.publishedAt,
    url: primaryUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': primaryUrl,
    },
    startDate: dateBroadcast,
    endDate,
    isLiveBroadcast: liveVideoStatus === 'LIVE',
    closedCaptioning,
  };

  return buildVideoObjectLD(videoObjectData, { includePublicationData, includeMainEntityOfPage });
}
